import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ShieldCheck, Loader2, Eye, Check, X, User, Phone, Building2,
  Calendar, FileText, Image as ImageIcon
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending:   'bg-blue-100 text-blue-700 border-blue-200',
  submitted: 'bg-amber-100 text-amber-700 border-amber-200',
  verified:  'bg-green-100 text-green-700 border-green-200',
  rejected:  'bg-red-100 text-red-700 border-red-200'
};

const AdminOnboardings = () => {
  const token = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('submitted');
  const [viewing, setViewing] = useState(null);
  const [docs, setDocs] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const r = await axios.get(`${API}/admin/tenant-onboardings`, { headers, params });
      setItems(r.data.items || []);
      setStats(r.data.stats || { total: 0, by_status: {} });
    } catch (e) {
      toast({ title: 'Could not load verifications', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openReview = async (item) => {
    setViewing(item);
    setDocs(null);
    try {
      const r = await axios.get(`${API}/admin/tenant-onboardings/${item.id}/documents`, { headers });
      setDocs(r.data);
    } catch (e) {
      toast({ title: 'Could not load docs', variant: 'destructive' });
    }
  };

  const approve = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/tenant-onboardings/${id}/approve`, {}, { headers });
      toast({ title: 'Approved ✅' });
      setViewing(null);
      fetchData();
    } catch (e) {
      toast({ title: 'Approve failed', variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id) => {
    const reason = window.prompt('Why is this being rejected? (shown to PG owner)');
    if (reason === null) return;
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/tenant-onboardings/${id}/reject`, { reason }, { headers });
      toast({ title: 'Rejected' });
      setViewing(null);
      fetchData();
    } catch (e) {
      toast({ title: 'Reject failed', variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-onboardings-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <ShieldCheck className="text-emerald-500" /> Tenant Verifications
        </h1>
        <p className="text-gray-600 mt-1">
          Review Aadhaar uploads from tenants who onboarded via Kolly's WhatsApp link.
        </p>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Total" value={stats.total || 0} color="from-slate-700 to-slate-900" />
        <Kpi label="Pending invite" value={stats.by_status?.pending || 0} color="from-blue-500 to-blue-700" />
        <Kpi label="Submitted" value={stats.by_status?.submitted || 0} color="from-amber-500 to-amber-700" highlight />
        <Kpi label="Verified" value={stats.by_status?.verified || 0} color="from-green-500 to-green-700" />
        <Kpi label="Rejected" value={stats.by_status?.rejected || 0} color="from-red-500 to-red-700" />
      </div>

      {/* Status filter */}
      <Card className="p-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-500 font-semibold mr-2">Filter:</span>
          {['', 'submitted', 'pending', 'verified', 'rejected'].map((s) => (
            <button
              key={s || 'all'}
              data-testid={`onb-filter-${s || 'all'}`}
              onClick={() => setStatusFilter(s)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                statusFilter === s
                  ? 'bg-emerald-500 text-white border-emerald-500'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
              }`}
            >
              {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
            </button>
          ))}
        </div>
      </Card>

      {/* Rows */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="animate-spin mx-auto text-emerald-500" size={32} /></div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <ShieldCheck size={44} className="mx-auto text-gray-300 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900">No verifications here</h3>
            <p className="text-sm text-gray-500 mt-1">
              {statusFilter === 'submitted'
                ? "When tenants finish their onboarding form, they'll appear here for review."
                : "Try a different filter."}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {items.map((it) => (
              <div
                key={it.id}
                data-testid={`onb-row-${it.id}`}
                className="p-4 hover:bg-emerald-50/30 transition-colors flex items-center gap-3 flex-wrap"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                  {(it.tenant_name || '?').slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-gray-900">{it.tenant_name || 'Unknown tenant'}</p>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATUS_COLORS[it.status] || 'bg-gray-100 text-gray-700'}`}>
                      {it.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-sm text-gray-600 flex-wrap">
                    <span className="flex items-center gap-1 font-mono"><Phone size={12} />+91 {it.tenant_phone}</span>
                    <span className="flex items-center gap-1"><Building2 size={12} />{it.property_name}</span>
                    {it.aadhaar_number && (
                      <span className="flex items-center gap-1 font-mono text-xs">
                        <FileText size={12} />
                        {String(it.aadhaar_number).replace(/(.{4})/g, '$1 ').trim()}
                      </span>
                    )}
                    {it.submitted_at && (
                      <span className="flex items-center gap-1 text-xs text-gray-400">
                        <Calendar size={12} />
                        {new Date(it.submitted_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {it.status === 'submitted' && (
                    <Button
                      data-testid={`onb-review-${it.id}`}
                      size="sm"
                      onClick={() => openReview(it)}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1.5"
                    >
                      <Eye size={14} /> Review
                    </Button>
                  )}
                  {it.status === 'verified' && (
                    <span className="inline-flex items-center gap-1 text-green-600 text-sm font-semibold">
                      <Check size={14} /> Verified
                    </span>
                  )}
                  {it.status === 'pending' && (
                    <span className="text-[11px] text-gray-400 italic">Link not opened yet</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Review Modal */}
      <Dialog open={!!viewing} onOpenChange={() => setViewing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <ShieldCheck className="text-emerald-500" size={22} />
              Review — {viewing?.tenant_name}
            </DialogTitle>
          </DialogHeader>
          {!docs ? (
            <div className="py-10 text-center"><Loader2 className="animate-spin mx-auto text-emerald-500" /></div>
          ) : (
            <div className="space-y-4">
              {/* Identity summary */}
              <div className="grid grid-cols-2 gap-4 bg-emerald-50 rounded-xl p-4 text-sm">
                <InfoRow label="Name" value={viewing?.tenant_name} icon={User} />
                <InfoRow label="Phone" value={`+91 ${viewing?.tenant_phone}`} icon={Phone} />
                <InfoRow label="Aadhaar" value={viewing?.aadhaar_number ? String(viewing.aadhaar_number).replace(/(.{4})/g, '$1 ').trim() : '—'} icon={FileText} />
                <InfoRow label="Property" value={viewing?.property_name} icon={Building2} />
                <InfoRow label="DOB" value={docs.onboarding?.dob || '—'} icon={Calendar} />
                <InfoRow label="Address" value={docs.onboarding?.permanent_address || '—'} icon={Building2} />
              </div>

              {/* Documents */}
              <div>
                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-2">Uploaded documents</h3>
                {docs.documents?.length ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {docs.documents.map((d, idx) => (
                      <div key={d.id || `${d.type}-${idx}`} className="border rounded-xl overflow-hidden bg-gray-50">
                        <div className="bg-gray-100 px-3 py-1.5 text-xs font-semibold capitalize flex items-center gap-1">
                          <ImageIcon size={12} /> {d.type.replace(/_/g, ' ')}
                        </div>
                        <a href={d.data} target="_blank" rel="noreferrer">
                          <img src={d.data} alt={d.type} className="w-full h-48 object-cover hover:opacity-90 transition-opacity" />
                        </a>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No documents uploaded.</p>
                )}
              </div>

              {/* IP / agreement timestamp */}
              <div className="bg-slate-50 rounded-lg p-3 text-xs text-gray-600 font-mono">
                Submitted from IP: {docs.onboarding?.submitted_ip || 'unknown'} · Agreement accepted:{' '}
                {docs.onboarding?.agreement_accepted_at
                  ? new Date(docs.onboarding.agreement_accepted_at).toLocaleString('en-IN')
                  : '—'}
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t border-gray-100">
                <Button
                  data-testid="onb-reject-btn"
                  onClick={() => reject(viewing.id)}
                  disabled={busyId === viewing.id}
                  variant="outline"
                  className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                >
                  <X size={14} className="mr-1" /> Reject
                </Button>
                <Button
                  data-testid="onb-approve-btn"
                  onClick={() => approve(viewing.id)}
                  disabled={busyId === viewing.id}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Check size={14} className="mr-1" /> Approve
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Kpi = ({ label, value, color, highlight }) => (
  <div className={`bg-gradient-to-br ${color} rounded-xl p-3 text-white shadow ${highlight ? 'ring-2 ring-amber-300' : ''}`}>
    <p className="text-[10px] uppercase tracking-wider opacity-80 font-semibold">{label}</p>
    <p className="text-2xl font-bold mt-0.5">{value}</p>
  </div>
);

const InfoRow = ({ label, value, icon: Icon }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold flex items-center gap-1">
      {Icon && <Icon size={10} />} {label}
    </p>
    <p className="font-semibold text-gray-900 text-sm">{value || '—'}</p>
  </div>
);

export default AdminOnboardings;
